import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Tag } from 'antd';
import { AxiosResponse } from 'axios';
import { TbPlus, TbRoute } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { VirtualizedList as List, SkeletonList } from '@/shared';
import {
    getPipelineItemAvatarProps,
    getPipelineItemDescription,
    getPipelineItemKey,
    getPipelineItemTitle,
    PIPELINE_ITEM_DEFINITION_BY_COLLECTION,
    PipelineItemResourceCollection,
    tagPipelineItem,
} from '@/shared/pipelineItems';
import { ellipsisStyling } from '@/utilities';

interface ListItemProps {
    pipeline?: any;
    column: any;
    result: any;
    resource: PipelineItemResourceCollection;
    columns: any;
    setColumns: any;
}

const ListItem: FC<ListItemProps> = ({
    pipeline,
    column,
    result,
    resource,
    columns,
    setColumns,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const { token } = useAntDesignTheme();
    const definition = PIPELINE_ITEM_DEFINITION_BY_COLLECTION[resource];
    const normalizedResult = tagPipelineItem(result, definition.type);
    const avatarProps = getPipelineItemAvatarProps(normalizedResult, token.colorTextTertiary);

    const getAllAddedResourceIds = () => {
        // Extract all card IDs from the columns state
        return columns.reduce((acc: Set<string>, column: any) => {
            const cardIds = column.cards?.map((card: any) => getPipelineItemKey(card)) || [];

            cardIds.forEach((id: any) => acc.add(id));

            return acc;
        }, new Set<string>());
    };

    const isResourceAdded = (resourceId: string) => {
        // Check if the resource ID is in the added set
        const addedResourceIds = getAllAddedResourceIds();
        return addedResourceIds.has(`${definition.type}:${resourceId}`);
    };

    const [clicked, setClicked] = useState<boolean>(false);

    const handleAddToPipelineColumn = async () => {
        if (!column || !pipeline?.id) {
            return;
        }

        const columnIndex = columns.findIndex((col: any) => col.id === column.id);

        if (columnIndex === -1) {
            return;
        }

        if (isResourceAdded(result?.id)) {
            return;
        }

        const optimisticColumns = columns.map((col: any) =>
            col.id === column.id ? { ...col, cards: [...(col.cards ?? []), normalizedResult] } : col,
        );

        try {
            setClicked(true);
            setColumns(optimisticColumns);

            const res: AxiosResponse = await authorizedAxiosInstance.post(`/api/pipelines/${pipeline?.id}/items`, {
                type: definition.type,
                resourceId: result?.id,
                pipelineColumn: column?.id,
            });

            if (res.status === 200) {
                const newCard = tagPipelineItem(res.data, definition.type);

                setColumns((prev: any[]) =>
                    prev.map((col: any) =>
                        col.id === column.id
                            ? {
                                  ...col,
                                  cards: (col.cards ?? []).map((card: any) =>
                                      card.id === result?.id ? newCard : card,
                                  ),
                              }
                            : col,
                    ),
                );

                toast(`${definition.label} Added to Pipeline`, {
                    icon: <TbRoute size='1rem' />,
                    description: getPipelineItemTitle(newCard),
                });
            }
        } catch (error) {
            setColumns((prev: any[]) =>
                prev.map((col: any) =>
                    col.id === column.id
                        ? { ...col, cards: (col.cards ?? []).filter((card: any) => card.id !== result?.id) }
                        : col,
                ),
            );
            captureException(error);
        } finally {
            setClicked(false);
        }
    };

    return result?.id && !loading ? (
        <List.Item
            key={result?.id}
            extra={[
                <Button
                    type='dashed'
                    icon={<TbPlus size='1rem' style={{ marginRight: '0.25rem', verticalAlign: '-0.2rem' }} />}
                    loading={loading || clicked}
                    disabled={isResourceAdded(result?.id) || loading || clicked}
                    onClick={handleAddToPipelineColumn}
                >
                    Add
                </Button>,
            ]}
        >
            <List.Item.Meta
                avatar={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '0.25rem' }}>
                        <Avatar
                            size='large'
                            shape='square'
                            src={avatarProps.src}
                            icon={avatarProps.icon}
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    </div>
                }
                title={
                    <>
                        {getPipelineItemTitle(normalizedResult)}
                        {definition.type === 'investment' && (
                            <Tag style={{ borderColor: 'transparent', marginLeft: '0.5rem' }}>
                                {result?.exchange}: <span style={{ fontWeight: 'normal' }}>{result?.tickerSymbol}</span>
                            </Tag>
                        )}
                    </>
                }
                description={
                    <div style={{ ...ellipsisStyling, maxWidth: '20rem', overflow: 'hidden', display: 'block' }}>
                        {getPipelineItemDescription(normalizedResult) ?? definition.description}
                    </div>
                }
            />
        </List.Item>
    ) : (
        <SkeletonList listLength={1} />
    );
};

export default ListItem;
